<template>
  <div class="page-vertical-container">
    <div class="page-header">
      <div class="page-header-left">
        <img src="@/assets/images/chatnav_logo.svg" alt="ChatNav Logo" class="dashboard-logo">
        <div class="title">Your AI Agents</div>
        <nav class="main-nav" v-if="isHubAdmin">
          <router-link to="/home" class="nav-link">Servers</router-link>
          <router-link to="/users" class="nav-link">Users</router-link>
          <router-link to="/admin" class="nav-link">Admin</router-link>
        </nav>
      </div>
      <div class="page-header-right">
        <div class="user-info">
          <span v-if="isHubAdmin" class="hub-admin-badge" title="You are a ChatNav Hub Admin">Hub Admin</span>
          <a v-if="isHubAdmin" href="https://troskovnici.dominvest.chatnav.net/" target="_blank" class="admin-tool-link">DI Plan Troškovnici</a>
          <span class="username">{{ username }}</span>
          <button class="logout-btn" @click="logout">Logout</button>
        </div>
      </div>
    </div>
    <div class="page-content">
  <AgentCards
    :servers="servers"
    :currentUserId="currentUserId"
    :currentUsername="username"
    :isHubAdmin="isHubAdmin"
    @deleted="handleAgentDeleted"
    @refresh="fetchServers"
    @create-agent="showCreateAgent = true"
  />
      <NewAgent
        v-if="showCreateAgent"
        :isHubAdmin="isHubAdmin"
        :key="isHubAdmin + '-' + showCreateAgent"
        @submit="handleCreateAgent"
        @close="showCreateAgent = false"
      />
    </div>
  </div>
</template>

<script>
import AgentCards from './AgentCards.vue'
import NewAgent from './NewAgent.vue'
import { useToast } from 'vue-toastification';
import { apiFetch } from '@/services/api';
import { getAccessToken, decodeToken, clearTokens } from '@/services/auth';
const toast = useToast();

export default {
  name: 'HomePage',
  components: { AgentCards, NewAgent },
  data() {
    return {
      servers: [],
      showCreateAgent: false,
      isHubAdmin: false,
      username: '',
      currentUserId: '',
      apiBase: process.env.VUE_APP_API_BASE_URL || '',
      eventSource: null
    }
  },
  async mounted() {
    await this.fetchServers();
    this.connectToServerStatusUpdates();
  },
  beforeUnmount() {
    this.disconnectFromServerStatusUpdates();
  },
  methods: {
    async handleAgentDeleted(payload = {}) {
      const { agentName, warnings } = payload;
      const label = agentName ? `Agent "${agentName}"` : 'Agent';
      if (warnings && warnings.length) {
        toast.warning(
          `${label} was removed from ChatNav Hub, but some cleanup steps failed: ` +
          `${warnings.join('; ')}. Please check manually to avoid leftover resources ` +
          '(e.g. Hetzner server still running or DNS subdomain still registered).',
          { timeout: false }
        );
      } else {
        toast.success(`${label} deleted successfully.`);
      }
      await this.fetchServers();
    },
    async fetchServers() {
      // Decode JWT to get hub_admin
      const payload = decodeToken(getAccessToken());
      if (payload) {
        this.isHubAdmin = !!payload.hub_admin;
        this.username = payload.username || payload.sub || '';
        this.currentUserId = payload.user_id || '';
      } else {
        this.isHubAdmin = false;
        this.username = '';
        this.currentUserId = '';
      }
      // Fetch servers
      const response = await apiFetch('/api/user-servers');
      if (response.ok) {
        const servers = await response.json();
        this.servers = servers.slice().sort((a, b) => a.name.localeCompare(b.name));
      }
    },
    handleCreateAgent(config) {
      console.log('New agent config submitted:', config);
      // You can handle backend creation here in the future
      this.showCreateAgent = false;
    },
    logout() {
      clearTokens();
      window.location.reload();
    },
    connectToServerStatusUpdates() {
      try {
        const token = getAccessToken();
        if (!token) {
          console.warn('No auth token available for SSE connection');
          return;
        }

        // Close existing connection if any
        this.disconnectFromServerStatusUpdates();

        // Create new EventSource connection (EventSource doesn't support custom headers)
        const url = `${this.apiBase}/api/server-status-stream?token=${encodeURIComponent(token)}`;
        this.eventSource = new EventSource(url);

        this.eventSource.onopen = () => {
          console.log('SSE connection established for server status updates');
        };

        this.eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleServerStatusUpdate(data);
          } catch (e) {
            console.error('Error parsing SSE message:', e);
          }
        };

        this.eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          // Attempt to reconnect after a delay
          setTimeout(() => {
            if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
              console.log('Attempting to reconnect SSE...');
              this.connectToServerStatusUpdates();
            }
          }, 5000);
        };

      } catch (error) {
        console.error('Failed to establish SSE connection:', error);
      }
    },
    disconnectFromServerStatusUpdates() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
        console.log('SSE connection closed');
      }
    },
    handleServerStatusUpdate(data) {
      if (data.type === 'server_status_change') {
        const serverData = data.data;
        const server = this.servers.find(s => s.server_id === serverData.server_id);

        if (server) {
          // Update server status
          const oldStatus = server.status;
          server.status = serverData.new_status;

          // Show toast notification with error message if present
          let message = `${serverData.server_name} status changed from ${oldStatus} to ${serverData.new_status}`;
          if (serverData.new_status === 'error' && serverData.error_message) {
            message = `${serverData.server_name}: ${serverData.error_message}`;
            toast.error(message);
          } else if (serverData.new_status === 'error') {
            toast.error(message);
          } else {
            toast.info(message);
          }

          // Add visual highlight effect
          this.$nextTick(() => {
            const serverElement = document.querySelector(`[data-server-id="${serverData.server_id}"]`);
            if (serverElement) {
              serverElement.classList.add('status-updated');
              setTimeout(() => {
                serverElement.classList.remove('status-updated');
              }, 2000);
            }
          });
        }
      } else if (data.type === 'connected') {
        console.log('SSE connection confirmed:', data.message);
      } else if (data.type === 'heartbeat') {
        // Heartbeat - connection is alive
        console.debug('SSE heartbeat received');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/home-page.scss";
</style>